import { FC, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Alert, Box, Button, Link, Typography, Skeleton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import WarningAmberIcon from '../icons/WarningAmberIcon';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';

import { useClaimsStore } from '../../hooks/useClaimsStore';
import { useChatCategoryStore } from '../../hooks/useChatCategoryStore';
import { Claim, ClaimStatus, ClaimLogEntry, EdiObject, ClaimDetailResponse, PreDefaultQuestionType, ClaimAppealLetter, ClaimAppeal, ActionType, ClaimLink, createPlaceholderClaim } from '../../types/claims';
import StatusChip from './components/table-components/StatusChip';
import AskReveActionMenu from './components/AskReveActionMenu';
import ClaimKeyInfoSection from './components/edi-components/ClaimKeyInfoSection';
import EdiObjectCard from './components/edi-components/EdiObjectCard';
import SimpleNavCard from './components/edi-components/SimpleNavCard';
import CardChip from './components/edi-components/CardChip';
import { MANUAL_PAYMENT_CHIP, MATCHED_MANUAL_CHIP, getEdiObjectLabel, getEdiObjectChip, getEdiObjectDate, getEdiObjectAmounts, getEdiObjectTransactionDate } from './components/edi-components/ediObjectHelpers';
import AppealLetterCard, { formatVersionCount } from './components/edi-components/record-appeal/AppealLetterCard';
import AppealLetterPlaceholderCard from './components/edi-components/record-appeal/AppealLetterPlaceholderCard';
import AppealLetterDetail from './components/edi-components/record-appeal/AppealLetterDetail';
import AppealCard from './components/edi-components/record-appeal/AppealCard';
import AppealDetail from './components/edi-components/record-appeal/AppealDetail';
import RecordAppealDialog from './components/edi-components/RecordAppealDialog';
import RecordPaymentDialog from './components/edi-components/RecordPaymentDialog';
import ManualPaymentDetail from './components/edi-components/record-payment/ManualPaymentDetail';
import { isManualPayment } from './components/edi-components/record-payment/manualPayments';
import DuplicatePaymentWarningCard from './components/edi-components/join-payment/DuplicatePaymentWarningCard';
import MatchedManualPaymentNote from './components/edi-components/join-payment/MatchedManualPaymentNote';
import { resolveDuplicatePayments } from './components/edi-components/join-payment/joinPayments';
import { getMismatchWarnings, getPossibleDuplicatePayments } from './claimDataWarnings';
import { actionCaption, actionTitle, isAppealAction } from './components/edi-components/claimAppeals';
import { buildClaimTimeline, getFirstAppealLetter } from './components/edi-components/claimTimeline';
import LinkClaimDialog from './components/edi-components/claim-link/LinkClaimDialog';
import UnlinkClaimDialog from './components/edi-components/claim-link/UnlinkClaimDialog';
import ResubmissionBanner from './components/edi-components/claim-link/ResubmissionBanner';
import { canLinkAnySide, claimTag, getLinkedRemits, getOwnEdiObjects, getResubmitAction } from './components/edi-components/claim-link/claimLinks';
import EdiClaimViewer from '../viewers/EdiClaimViewer/EdiClaimViewer';
import Row from '../ui/core/Row';
import { GradientButton } from '../ui/core/GradientButton';
import HeaderActions from '../ui/HeaderActions';
import SidebarDelimiter from '../SidebarDelimiter';
import { useChatStore } from '../../hooks/useChatStore';
import { formatDisplayDate, formatInstant } from '../helpers/dataHelper';
import { useCapabilities } from '../../contexts/permissions/useCapabilities';
import { useAuth } from '../../contexts/AuthContext';
import { ClaimNotFound } from './components/ClaimNotFound';
import { useAppStore } from '../../hooks/useAppStore';

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

// Sort EDI objects: Claims (837) first, then rest sorted by transaction date
const sortEdiObjects = (ediObjects: EdiObject[]): EdiObject[] => {
    if (!ediObjects || ediObjects.length <= 1) return ediObjects;

    return [...ediObjects].sort((a, b) => {
        // Claims (837) always come first
        if (a.objectType === 'CLAIM' && b.objectType !== 'CLAIM') return -1;
        if (a.objectType !== 'CLAIM' && b.objectType === 'CLAIM') return 1;

        // Both are claims or both are non-claims: sort by transaction date
        const dateA = getEdiObjectTransactionDate(a);
        const dateB = getEdiObjectTransactionDate(b);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateA.getTime() - dateB.getTime();
    });
};

// Utility function to scroll to a denial code in the EDI viewer
const scrollToDenialCode = (code: string) => {
    if (!code) return;
    
    // Step 1: Find and switch to the PAYMENT (835) object card if it exists
    // Each Claims & Remits card is a div with an explicit role="button"
    const clickIfPaymentCard = (el: Element): boolean => {
        // A manual payment has no adjustments or service lines to scroll to
        if ((el as HTMLElement).dataset.manualPayment) return false;
        const text = el.textContent || '';
        // Check for "835 Payment" or just "Payment" in the card text
        if (text.includes('835 Payment') || (text.includes('Payment') && !text.includes('Claim'))) {
            (el as HTMLElement).click();
            return true;
        }
        return false;
    };

    let paymentTabFound = false;
    document.querySelectorAll('[role="button"]').forEach((card) => {
        if (clickIfPaymentCard(card)) paymentTabFound = true;
    });

    // Step 2: Wait for Payment835Viewer to render, then determine which tab to switch to
    const switchToTab = () => {
        // Try multiple times in case the component hasn't rendered yet
        let attempts = 0;
        const maxAttempts = 15;

        const trySwitchTab = () => {
            attempts++;

            // First check if Payment835Viewer is rendered by looking for its tabs
            const adjustmentsTab = document.querySelector('[data-tab="adjustments"]') as HTMLElement;
            const serviceLinesTab = document.querySelector('[data-tab="serviceLines"]') as HTMLElement;

            if (!adjustmentsTab && !serviceLinesTab && attempts < maxAttempts) {
                // Viewer not ready yet, retry
                setTimeout(trySwitchTab, 100);
                return;
            }

            // Strategy: Check currently active tab FIRST, then try other tabs
            const normalizedCode = code.trim();

            // Determine if this is a RARC code vs CARC code
            // RARC codes can have "RARC-" prefix (like "RARC-N782") or just the code (like "N782")
            // CARC codes have format like "CO-197" or "PR-2"
            // RARC codes ONLY exist in service lines (inside expandable rows), never in adjustments tab
            const isRarcCode = normalizedCode.startsWith('RARC-') || (!normalizedCode.includes('-') && !['CO', 'PR', 'OA', 'CR', 'PI'].some(prefix => normalizedCode.toUpperCase().startsWith(prefix + '-')));

            // Helper to check if code exists in current DOM
            // For service lines: also checks data-codes attributes on <tr> rows
            // (elements may not exist in DOM when service lines are collapsed)
            const checkCodeExists = (isServiceLine: boolean): boolean => {
                if (isServiceLine) {
                    // Use data-codes on <tr> rows — works even when rows are collapsed
                    const searchTokens = buildCodeSearchTokens(normalizedCode);
                    const allRows = document.querySelectorAll<HTMLElement>('tr[data-codes]');
                    for (const row of Array.from(allRows)) {
                        const codes = (row.getAttribute('data-codes') || '').split(',');
                        for (const token of searchTokens) {
                            if (codes.some(c => c === token || c.endsWith(`-${token}`))) {
                                return true;
                            }
                        }
                    }
                    // Also check rendered elements (in case rows are already expanded)
                    const possibleIds = buildPossibleIds(normalizedCode);
                    for (const id of possibleIds) {
                        if (id.includes('-line-') && document.getElementById(id)) {
                            return true;
                        }
                    }
                    return false;
                } else {
                    // For adjustments tab, check rendered elements directly
                    const possibleIds = buildPossibleIds(normalizedCode);
                    for (const id of possibleIds) {
                        if (!id.includes('-line-') && document.getElementById(id)) {
                            return true;
                        }
                    }
                    return false;
                }
            };

            // Detect which tab is currently active by checking for active styling
            const isServiceLinesActive = serviceLinesTab?.classList.contains('border-[#00A89D]') ||
                                        serviceLinesTab?.getAttribute('aria-selected') === 'true';
            const isAdjustmentsActive = adjustmentsTab?.classList.contains('border-[#00A89D]') ||
                                       adjustmentsTab?.getAttribute('aria-selected') === 'true';

            // For RARC codes, always go directly to service lines (that's the only place they exist)
            // The checkCodeExists for RARC may return false if rows are collapsed, but performScroll
            // will expand the rows and find the code
            if (isRarcCode) {
                if (serviceLinesTab) {
                    serviceLinesTab.click();
                    setTimeout(() => {
                        performScroll(code);
                    }, 300);
                } else {
                    performScroll(code);
                }
                return;
            }

            // Strategy 1: If user is already on a tab, check that tab first (for CARC codes)
            if (isServiceLinesActive) {
                // Currently on Service Lines, check it first
                setTimeout(() => {
                    if (checkCodeExists(true)) {
                        // Found in current service lines tab, scroll to it
                        performScroll(code);
                    } else {
                        // Not in service lines, try adjustments tab
                        if (adjustmentsTab) {
                            adjustmentsTab.click();
                            setTimeout(() => {
                                performScroll(code);
                            }, 200);
                        }
                    }
                }, 100);
            } else if (isAdjustmentsActive) {
                // Currently on Adjustments, check it first
                setTimeout(() => {
                    if (checkCodeExists(false)) {
                        // Found in current adjustments tab, scroll to it
                        performScroll(code);
                    } else {
                        // Not in adjustments, try service lines tab
                        if (serviceLinesTab) {
                            serviceLinesTab.click();
                            setTimeout(() => {
                                performScroll(code);
                            }, 200);
                        }
                    }
                }, 100);
            } else {
                // Strategy 2: No tab is active yet, prioritize service lines (more detailed)
                if (serviceLinesTab) {
                    serviceLinesTab.click();

                    // Wait for service lines to render, then check
                    setTimeout(() => {
                        if (checkCodeExists(true)) {
                            // Found in service lines, scroll to it
                            performScroll(code);
                        } else {
                            // Not in service lines, try adjustments tab
                            if (adjustmentsTab) {
                                adjustmentsTab.click();
                                setTimeout(() => {
                                    performScroll(code);
                                }, 200);
                            }
                        }
                    }, 300);
                } else if (adjustmentsTab) {
                    // Only adjustments tab available
                    adjustmentsTab.click();
                    setTimeout(() => {
                        performScroll(code);
                    }, 200);
                } else if (attempts < maxAttempts) {
                    // Retry after a short delay
                    setTimeout(trySwitchTab, 100);
                } else {
                    // If tab not found after max attempts, try scrolling anyway
                    performScroll(code);
                }
            }
        };

        trySwitchTab();
    };
    
    const finish = (found: boolean) => {
        if (found) {
            // Wait for Payment835Viewer to render
            setTimeout(switchToTab, 300);
        } else {
            // No payment card found, try to switch to tab directly
            switchToTab();
        }
    };

    if (!paymentTabFound) {
        // The payment card may be hidden because we're already drilled into a different
        // card's detail view — go back to the card list and retry once it's rendered.
        const backButton = document.querySelector('button[aria-label="Back to Claim timeline"]') as HTMLElement | null;
        if (backButton) {
            backButton.click();
            setTimeout(() => {
                let found = false;
                document.querySelectorAll('[role="button"]').forEach((card) => {
                    if (clickIfPaymentCard(card)) found = true;
                });
                finish(found);
            }, 50);
            return;
        }
    }

    finish(paymentTabFound);
};

// Build the list of possible element IDs for a given denial code
const buildPossibleIds = (code: string): string[] => {
    const normalizedCode = code.trim();
    const possibleIds: string[] = [];

    if (normalizedCode.startsWith('RARC-')) {
        const actualCode = normalizedCode.substring(5);
        for (let lineIdx = 0; lineIdx < 20; lineIdx++) {
            possibleIds.push(`remark-${actualCode}-line-${lineIdx}`);
        }
        possibleIds.push(`remark-${actualCode}`);
    } else if (normalizedCode.includes('-')) {
        const [group, reasonCode] = normalizedCode.split('-');
        const groupMap: Record<string, string[]> = {
            'CO': ['CONTRACTUAL', 'CO'],
            'PR': ['PATIENT_RESPONSIBILITY', 'PR'],
            'OA': ['OTHER', 'OA'],
            'CR': ['CORRECTION', 'CR'],
            'PI': ['PAYOR_INITIATED', 'PI'],
        };
        const mappedGroups = groupMap[group.toUpperCase()] || [];
        const groups = mappedGroups.length > 0 ? mappedGroups : [group.toUpperCase()];

        for (const g of groups) {
            for (let lineIdx = 0; lineIdx < 20; lineIdx++) {
                possibleIds.push(`adjustment-${g}-${reasonCode}-line-${lineIdx}`);
            }
        }
        for (const g of groups) {
            possibleIds.push(`adjustment-${g}-${reasonCode}`);
        }
    } else {
        for (let lineIdx = 0; lineIdx < 20; lineIdx++) {
            possibleIds.push(`remark-${normalizedCode}-line-${lineIdx}`);
        }
        possibleIds.push(`remark-${normalizedCode}`);
    }
    return possibleIds;
};

// Build the search tokens for matching against data-codes on <tr> elements
const buildCodeSearchTokens = (code: string): string[] => {
    const normalizedCode = code.trim();
    const tokens: string[] = [];

    if (normalizedCode.startsWith('RARC-')) {
        tokens.push(normalizedCode.substring(5));
    } else if (normalizedCode.includes('-')) {
        const [group, reasonCode] = normalizedCode.split('-');
        const groupMap: Record<string, string[]> = {
            'CO': ['CONTRACTUAL', 'CO'],
            'PR': ['PATIENT_RESPONSIBILITY', 'PR'],
            'OA': ['OTHER', 'OA'],
            'CR': ['CORRECTION', 'CR'],
            'PI': ['PAYOR_INITIATED', 'PI'],
        };
        const mappedGroups = groupMap[group.toUpperCase()] || [];
        const groups = mappedGroups.length > 0 ? mappedGroups : [group.toUpperCase()];
        for (const g of groups) {
            tokens.push(`${g}-${reasonCode}`);
        }
    } else {
        tokens.push(normalizedCode);
    }
    return tokens;
};

const findElementsByIds = (possibleIds: string[]): HTMLElement[] => {
    const elements: HTMLElement[] = [];
    for (const id of possibleIds) {
        const el = document.getElementById(id);
        if (el) elements.push(el);
    }
    return elements;
};

const scrollAndHighlight = (elements: HTMLElement[]) => {
    if (elements.length === 0) return;
    elements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    const serviceLineRows = new Set<HTMLElement>();
    elements.forEach((element) => {
        highlightElement(element);
        if (element.id.includes('-line-')) {
            const parent = element.closest('tr[role="button"]') as HTMLElement
                || element.closest('tr[aria-expanded]') as HTMLElement
                || element.closest('tr') as HTMLElement;
            if (parent) serviceLineRows.add(parent);
        }
    });
    serviceLineRows.forEach((row) => highlightElement(row));
};

const performScroll = (code: string) => {
    const possibleIds = buildPossibleIds(code);

    // Step 1: Try to find elements already in the DOM (rows may be expanded)
    const matchingElements = findElementsByIds(possibleIds);

    if (matchingElements.length > 0) {
        // Elements found — expand any collapsed parent rows, then scroll
        matchingElements.forEach((element) => {
            if (element.id.includes('-line-')) {
                const parent = element.closest('tr[role="button"]') as HTMLElement
                    || element.closest('tr[aria-expanded]') as HTMLElement;
                if (parent && parent.getAttribute('aria-expanded') === 'false') {
                    parent.click();
                }
            }
        });
        setTimeout(() => scrollAndHighlight(matchingElements),
            matchingElements.some(el => el.id.includes('-line-')) ? 300 : 0);
        return;
    }

    // Step 2: Elements not in DOM — service lines are collapsed.
    // Use data-codes on <tr> rows to find which specific lines contain this code,
    // expand only those, then scroll to the target elements.
    const searchTokens = buildCodeSearchTokens(code);
    const allRows = document.querySelectorAll<HTMLElement>('tr[data-codes]');
    const matchingRows: HTMLElement[] = [];

    allRows.forEach((row) => {
        const codes = row.getAttribute('data-codes') || '';
        for (const token of searchTokens) {
            if (codes.split(',').some(c => c === token || c.endsWith(`-${token}`))) {
                matchingRows.push(row);
                break;
            }
        }
    });

    if (matchingRows.length > 0) {
        // Expand only the matching collapsed rows
        matchingRows.forEach((row) => {
            if (row.getAttribute('aria-expanded') === 'false') {
                row.click();
            }
        });

        // Wait for render, then find and scroll to the actual elements
        setTimeout(() => {
            const retryElements = findElementsByIds(possibleIds);
            if (retryElements.length > 0) {
                scrollAndHighlight(retryElements);
            } else {
                // Fallback: scroll to the first matching row itself
                matchingRows[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                matchingRows.forEach((row) => highlightElement(row));
            }
        }, 400);
        return;
    }

    // Step 3: Not found in service lines at all — try section-level fallback
    const targetSection = document.getElementById('adjustments-section')
        || document.querySelector('[id^="adjustments-section-line-"]')
        || document.querySelector('[id^="remarks-section-line-"]');
    if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

const highlightElement = (element: HTMLElement) => {
    element.style.transition = 'background-color 0.3s';
    element.style.backgroundColor = '#fff3cd';
    setTimeout(() => {
        element.style.backgroundColor = '';
    }, 2000);
};

// An admin action in the Claim timeline header, with the Ask Reve pill's gradient
const TimelineActionButton: FC<{ onClick: () => void; children: ReactNode }> = ({ onClick, children }) => (
    <GradientButton
        size="small"
        onClick={onClick}
        sx={{
            height: 24,
            px: 1,
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'common.white',
            minWidth: "100px",
        }}
    >
        {children}
    </GradientButton>
);

interface ClaimPreviewProps {
    viewerPosition?: "left" | "right";
    onTogglePosition?: () => void;
    claimId?: string | null;
    onCloseCallback?: () => void;
}

const ClaimPreview: FC<ClaimPreviewProps> = observer(({ viewerPosition, onTogglePosition, claimId, onCloseCallback }) => {
    const theme = useTheme();
    const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();
    const location = useLocation();
    const chatStore = useChatStore();
    const {
        claimToPreview: claimToPreviewStore,
        setClaimToPreview,
        setClaimActivePredefaultQuestion,
        markClaimAppealed,
        updateLoadedClaim,
    } = useClaimsStore();
    const { 
        sidePanelWidth, 
        isSidePanelResizing, 
        setSidePanelWidth, 
        setIsSidePanelResizing 
    } = useChatCategoryStore();
    const { canAskReve } = useCapabilities();
    // Recording appeals and manual payments is admin-only (the API enforces it too); everyone sees them
    const { isAdmin } = useAuth();
    const appStore = useAppStore();
    
    const [selectedTab, setSelectedTab] = useState(0);
    // Whether we're drilled into a card's detail (vs. showing the Claims & Remits card list)
    const [isDetailView, setIsDetailView] = useState(false);
    // Chat id of the appeal letter card that's open; takes precedence over selectedTab
    const [selectedLetterChatId, setSelectedLetterChatId] = useState<string | null>(null);
    // Id of the Appealed card that's open; like the letter, takes precedence over selectedTab
    const [selectedAppealId, setSelectedAppealId] = useState<string | null>(null);
    const [isRecordAppealOpen, setIsRecordAppealOpen] = useState(false);
    const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
    const [isLinkClaimOpen, setIsLinkClaimOpen] = useState(false);
    // The link dialog opened straight after recording a correct and resubmit
    const [isLinkAfterRecording, setIsLinkAfterRecording] = useState(false);
    const [unlinkTarget, setUnlinkTarget] = useState<ClaimLink | null>(null);
    // Index (in linkedRemits) of the corrected claim's remit that's open, read-only
    const [selectedLinkedRemitIndex, setSelectedLinkedRemitIndex] = useState<number | null>(null);

    const detailData = chatStore.claimDetailData;
    const loading = chatStore.isClaimDataLoading;
    const error = chatStore.claimDetailError;
    // Refs for resize calculations
    const positionRef = useRef(viewerPosition || "right");
    // When selectEdiTab fires before detailData loads (e.g. panel was closed), store fileName to apply when detailData loads
    const pendingEdiFileNameRef = useRef<string | null>(null);

    const claimToPreview = {
        ...detailData?.claim || {},
        ...claimToPreviewStore || (claimId ? { claimNumber: claimId } as Claim : null)
    } as Claim;
    
    useEffect(() => {
        positionRef.current = viewerPosition || "right";
    }, [viewerPosition]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const containerRef = document.getElementById("chat-container-ref")
    if (!isSidePanelResizing || !containerRef) return;
    
    const containerRect = containerRef.getBoundingClientRect();
    const containerWidth = containerRect.width - 12;
    const newWidth =
      positionRef.current === "left"
        ? e.clientX - containerRect.left
        : containerRect.right - e.clientX;
    const newPercent = (newWidth / containerWidth) * 100;

    setSidePanelWidth(newPercent);
  }, [isSidePanelResizing]);

  const handleMouseUp = useCallback(() => {
    setIsSidePanelResizing(false);
  }, []);

  useEffect(() => {
    if (isSidePanelResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isSidePanelResizing, handleMouseMove, handleMouseUp]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsSidePanelResizing(true);
  }, []);

    // Reset the Claims & Remits card/detail state whenever the previewed claim changes
    // (including switching directly from one claim to a different one, not just closing).
    useEffect(() => {
        setSelectedTab(0);
        setIsDetailView(false);
        setSelectedLetterChatId(null);
        setSelectedAppealId(null);
        setSelectedLinkedRemitIndex(null);
        setIsRecordAppealOpen(false);
        setIsRecordPaymentOpen(false);
        setIsLinkClaimOpen(false);
        setUnlinkTarget(null);
    }, [claimToPreview?.claimNumber]);

    // Listen for selectEdiTab event to switch to the correct tab
    useEffect(() => {
        const handleSelectEdiTab = (event: CustomEvent<{ fileName: string, claim?: ClaimDetailResponse | undefined }>) => {
            const { fileName, claim } = event?.detail || {};
            if (!fileName || !claim?.ediObjects?.length) return;

            const ediList = getOwnEdiObjects(claim?.ediObjects)

            // Use sorted EDI objects to find the correct tab index
            const sorted = sortEdiObjects(ediList);
            const fileNameLower = fileName.toLowerCase();
            const matchingIndex = sorted.findIndex((ediObj: EdiObject) => {
                // A file links to its own EDI object, never to a manual payment
                if (isManualPayment(ediObj)) return false;
                const objectType = ediObj.objectType?.toLowerCase() || '';

                // Check if fileName contains the objectType
                if (fileNameLower.includes(objectType)) return true;

                // Check if objectType keywords appear in fileName
                // e.g., "claim" in fileName matches "CLAIM" objectType
                const keywords = objectType.split('_'); // Handle types like "CLAIM_STATUS"

                return keywords.some(keyword => fileNameLower.includes(keyword)) || ediObj?.transaction?.fileInfo?.name === fileName;
            });

            if (matchingIndex !== -1) {
                // EDI tabs are first, warnings tab is last (no offset needed)
                setSelectedTab(matchingIndex);
                setSelectedLetterChatId(null);
                setSelectedAppealId(null);
                setSelectedLinkedRemitIndex(null);
                setIsDetailView(true);
            }
        };

        window.addEventListener('selectEdiTab', handleSelectEdiTab as EventListener);
        
        return () => {
            window.removeEventListener('selectEdiTab', handleSelectEdiTab as EventListener);
        };
    }, [claimId, detailData?.ediObjects]);

    // Apply pending EDI tab when ClaimPreview was closed and opens (pending in ChatStore) or event fired before detailData (ref)
    useEffect(() => {
        const pendingFileName = chatStore.pendingEdiFileNameForClaimTab ?? pendingEdiFileNameRef.current;
        if (!pendingFileName || !detailData?.ediObjects?.length) return;

        const sorted = sortEdiObjects(getOwnEdiObjects(detailData.ediObjects));
        const fileNameLower = pendingFileName.toLowerCase();
        const matchingIndex = sorted.findIndex((ediObj: EdiObject) => {
            if (isManualPayment(ediObj)) return false;
            const objectType = ediObj.objectType?.toLowerCase() || '';
            if (fileNameLower.includes(objectType)) return true;
            const keywords = objectType.split('_');
            return keywords.some((keyword: string) => fileNameLower.includes(keyword));
        });

        if (matchingIndex !== -1) {
            // EDI tabs are first, warnings tab is last (no offset needed)
            setSelectedTab(matchingIndex);
            setSelectedLetterChatId(null);
            setSelectedAppealId(null);
            setSelectedLinkedRemitIndex(null);
            setIsDetailView(true);
        }
        chatStore.setPendingEdiFileNameForClaimTab(null);
        pendingEdiFileNameRef.current = null;
    }, [detailData?.ediObjects, chatStore.pendingEdiFileNameForClaimTab]);

    // Used by AskReveActionMenu: sets claim context, then immediately
    // sends the given prompt (quick action or free-text) instead of waiting for user input.
    const handleAskReveAction = async (question: string, agentName?: string) => {
        if (!claimToPreview) return;

        const claimNumber = claimToPreview.claimNumber;

        if (claimNumber && claimNumber !== chatStore.claimIdToPreview) {
            chatStore.setClaimIdToPreview(claimNumber);
        }
        chatStore.setIsAborting(false);

        const redirectId = await chatStore.askQuestion(question, undefined, agentName, true);
        if (redirectId) {
            navigate(`/channels/${redirectId}`);
        }

        setClaimActivePredefaultQuestion(null);
    };

    // Used by AskReveActionMenu: snapshots the claim number into the pending predefault
    // question so claim context survives the panel closing before Send, then sets the
    // pending predefault question so the Claims page can show a confirmation preview
    // (in place of the claims table) with a real ChatInput, instead of firing it off immediately.
    const handleAskRevePredefaultQuestion = (predefaultQuestion: PreDefaultQuestionType) => {
        if (!claimToPreview) return;
        setClaimActivePredefaultQuestion({
            ...predefaultQuestion,
            claimNumber: claimToPreview.claimNumber,
        });
    };

    const handleClose = () => {
        setClaimToPreview(null);
        chatStore.setClaimIdToPreview(null)
        chatStore.clearPanelViewHistory();
        setClaimActivePredefaultQuestion(null);
        if(onCloseCallback){
            onCloseCallback();
        }

        if(location.pathname?.includes("/claims")){
            chatStore.setClaimModeEnabled(false);
        }
    };

    const handleSelectCard = (index: number) => {
        setSelectedTab(index);
        setSelectedLetterChatId(null);
        setSelectedAppealId(null);
        setSelectedLinkedRemitIndex(null);
        setIsDetailView(true);
    };

    const handleSelectLetter = (chatId: string) => {
        setSelectedLetterChatId(chatId);
        setSelectedAppealId(null);
        setSelectedLinkedRemitIndex(null);
        setIsDetailView(true);
    };

    const handleSelectAppeal = (appealId: string) => {
        setSelectedAppealId(appealId);
        setSelectedLetterChatId(null);
        setSelectedLinkedRemitIndex(null);
        setIsDetailView(true);
    };

    const handleSelectLinkedRemit = (index: number) => {
        setSelectedLinkedRemitIndex(index);
        setSelectedAppealId(null);
        setSelectedLetterChatId(null);
        setIsDetailView(true);
    };

    // Opens the other claim of a link in this panel
    const handleOpenClaim = (claimNumber: string) => {
        const isClaimsPage = !!claimToPreviewStore;
        if (isClaimsPage) setClaimToPreview(createPlaceholderClaim(claimNumber));
        chatStore.setIsClaimDataLoading(true);
        chatStore.setClaimIdToPreview(claimNumber);
        void chatStore.loadClaimDetail(claimNumber).then((data) => {
            if (isClaimsPage && data?.claim) setClaimToPreview(data.claim);
        });
    };

    // A link or unlink changes both banners, the timeline (the corrected claim's remits) and the log
    const handleLinkChanged = () => {
        setIsDetailView(false);
        setSelectedAppealId(null);
        setSelectedLinkedRemitIndex(null);
        const claimNumber = claimToPreview?.claimNumber;
        if (claimNumber) void chatStore.refreshClaimDetail(claimNumber);
    };

    // Recording a correct and resubmit can go straight on to linking the corrected claim
    const handleLinkAfterRecording = () => {
        setIsRecordAppealOpen(false);
        setIsLinkAfterRecording(true);
        setIsLinkClaimOpen(true);
    };

    const openLinkDialog = () => {
        setIsLinkAfterRecording(false);
        setIsLinkClaimOpen(true);
    };

    // Show the saved appeal at once, then reload for what the server derived from it
    // (the log line, the Appealed flag). A null appeal is a duplicate: just reload.
    // The reload only lands if this claim is still open, and a failed one keeps the added appeal.
    const handleAppealRecorded = (appeal: ClaimAppeal | null, actionType: ActionType) => {
        if (appeal) chatStore.addClaimAppeal(appeal);
        // A correct and resubmit doesn't make the claim Appealed (REV-2628)
        if (claimToPreview?.id && isAppealAction(actionType)) markClaimAppealed(claimToPreview.id);
        const claimNumber = claimToPreview?.claimNumber;
        if (claimNumber) void chatStore.refreshClaimDetail(claimNumber);
    };

    // A manual payment changes the claim's timeline, paid amount and status: reload it,
    // and carry the new values to the table row and header (which read the store's claim)
    const refreshClaimAfterPayment = async () => {
        const claimNumber = claimToPreview?.claimNumber;
        if (!claimNumber) return;
        const data = await chatStore.refreshClaimDetail(claimNumber);
        if (!data?.claim?.id) return;
        const { paidAmount, paidInclResubmission, patientBalance, status, payer, denialReason, dataWarnings } = data.claim;
        updateLoadedClaim(data.claim.id, { paidAmount, paidInclResubmission, patientBalance, status, payer, denialReason, dataWarnings });
    };

    // A removed or joined manual payment leaves the timeline (and its warnings), so go back to it
    const handlePaymentLeftTimeline = () => {
        setIsDetailView(false);
        setSelectedTab(0);
        void refreshClaimAfterPayment();
    };

    // On an original claim, its detail also has the corrected claim's remits its fee counts,
    // tagged fromClaimNumber (REV-2657): read-only on the timeline, not this claim's own
    const ediObjects = getOwnEdiObjects(detailData?.ediObjects);
    const linkedRemits = getLinkedRemits(detailData?.ediObjects);

    // Sort EDI objects: Claims first, then rest by transaction date
    const sortedEdiObjects = sortEdiObjects(ediObjects);

    // Claim timeline: EDI objects plus a single appeal letter card, the first one generated
    const firstAppealLetter = getFirstAppealLetter(detailData?.appealLetters || []);
    const appealLetters: ClaimAppealLetter[] = firstAppealLetter ? [firstAppealLetter] : [];
    const appeals: ClaimAppeal[] = detailData?.appeals || [];
    // Links (REV-2629): this claim's action to its corrected claim, and/or this claim as some claim's corrected one
    const childLink = getResubmitAction(appeals)?.link ?? null;
    const childClaimNumber = childLink?.childClaimNumber ?? null;
    const resubmissionOf = detailData?.resubmissionOf ?? null;
    const shownLinkedRemits = linkedRemits && linkedRemits.fromClaimNumber === childClaimNumber ? linkedRemits : null;
    const timelineItems = buildClaimTimeline(sortedEdiObjects, appealLetters, !!(claimToPreview?.appealLetter ?? detailData?.claim?.appealLetter), appeals, shownLinkedRemits);
    const selectedLetter = (selectedLetterChatId && appealLetters.find(l => l.chatId === selectedLetterChatId)) || null;
    const selectedAppeal = (selectedAppealId && appeals.find(a => a.id === selectedAppealId)) || null;
    const selectedLinkedRemit = selectedLinkedRemitIndex !== null && shownLinkedRemits ? shownLinkedRemits.remits[selectedLinkedRemitIndex] ?? null : null;

    // Warnings from dataWarnings: attachment mismatches as text, possible duplicate payments as cards
    const claimWarnings: string[] = getMismatchWarnings(claimToPreview?.dataWarnings);
    const duplicatePayments = resolveDuplicatePayments(
        getPossibleDuplicatePayments(claimToPreview?.dataWarnings),
        ediObjects,
    );
    const warningCount = claimWarnings.length + duplicatePayments.length;
    const hasWarnings = warningCount > 0;

    // Log entries from the API
    const logEntries: ClaimLogEntry[] = detailData?.logEntries || [];
    const hasLogEntries = logEntries.length > 0;

    // Tab indices: EDI objects | Log | Warnings
    const logTabIndex = sortedEdiObjects.length;
    const warningsTabIndex = sortedEdiObjects.length + (hasLogEntries ? 1 : 0);
    const totalTabCount = sortedEdiObjects.length + (hasLogEntries ? 1 : 0) + (hasWarnings ? 1 : 0);

    useEffect(() => {
        if (!claimToPreview) {
            return;
        }

        if (selectedTab >= totalTabCount) {
            setSelectedTab(0);
        }
    }, [claimToPreview, totalTabCount, selectedTab]);

    const getCurrentEdiObject = (): EdiObject | null => {
        if (selectedLetter || selectedAppeal || selectedLinkedRemit) return null;
        if (!sortedEdiObjects.length) return null;
        if (hasLogEntries && selectedTab === logTabIndex) return null;
        if (hasWarnings && selectedTab === warningsTabIndex) return null;
        return sortedEdiObjects[selectedTab] || null;
    };

    const hasEdiCardList = timelineItems.length >= 1 || hasWarnings || hasLogEntries;
    const currentEdiObject = getCurrentEdiObject();
    const isCardSelected = !!(selectedLetter || selectedAppeal || selectedLinkedRemit);
    const isLogTabSelected = !isCardSelected && hasLogEntries && selectedTab === logTabIndex;
    const isWarningsTabSelected = !isCardSelected && hasWarnings && selectedTab === warningsTabIndex;
    const canRecordAppeal = isAdmin && !!claimToPreview?.id;
    const canManagePayments = isAdmin && !!claimToPreview?.id;
    // Linking and unlinking are admin-only (REV-2629, the API enforces it too); everyone sees the links
    const canManageLinks = isAdmin && !!claimToPreview?.id;
    const canLinkClaim = canManageLinks && canLinkAnySide(appeals, resubmissionOf);
    const claimTimelineActionsEnabled = appStore.isClaimCorrectFlagsEnabled;

    const currentPosition = viewerPosition || "right";

    return (
        <aside 
            className="h-full min-h-0 flex flex-row bg-white"
            style={{ 
                width: isTabletOrSmaller ? '100%' : `${sidePanelWidth}%`,
                flexShrink: 0,
                transition: isSidePanelResizing ? 'none' : 'width 150ms ease-out',
            }}
        >
            {!isTabletOrSmaller && currentPosition === 'right' && (
                <SidebarDelimiter 
                    onTogglePosition={onTogglePosition}
                    onResizeStart={handleMouseDown} 
                    isResizing={isSidePanelResizing} 
                />
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    flex: 1,
                    minWidth: 0,
                    position: 'relative',
                }}
            >
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'white'
                }}
            >
                {/* Top row: Claim ID, patient, payer, status and close button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Row sx={{ alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0, flexWrap: 'nowrap' }}>
                        {chatStore.canGoBackPanelView && (
                            <button
                                type="button"
                                onClick={() => chatStore.goBackPanelView()}
                                className="flex items-center justify-center rounded-[8px] h-[28px] w-[28px] text-gray-600 hover:bg-gray-100 shrink-0"
                                title="Back"
                                aria-label="Back to previous view"
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                            </button>
                        )}
                        {loading ? (
                            <>
                                <Skeleton width={150} height={24} />
                                <Skeleton width={80} height={20} sx={{ mt: 0.5 }} />
                            </>
                        ) : claimToPreview ? (
                            <>
                                <Typography fontWeight={600} noWrap sx={{ flexShrink: 0 }}>
                                    {claimToPreview?.claimNumber || claimId}
                                </Typography>

                                {claimToPreview?.patientName && (
                                    <>
                                        <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>·</Box>
                                        <Typography noWrap color="text.secondary" sx={{ fontSize: '0.875rem', flexShrink: 1, minWidth: 50, maxWidth: 200 }}>
                                            {claimToPreview.patientName}
                                        </Typography>
                                    </>
                                )}

                                {claimToPreview?.payer && (
                                    <>
                                        <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>·</Box>
                                        <Typography noWrap color="text.secondary" sx={{ fontSize: '0.875rem', flex: '1 1 0', minWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {claimToPreview.payer}
                                        </Typography>
                                    </>
                                )}

                                {claimToPreview?.status ?
                                    <StatusChip dotted status={claimToPreview.status as ClaimStatus} sx={{ flexShrink: 1, minWidth: 0, borderRadius: "50px" }} />
                                : null}

                                {hasWarnings && (
                                    <Tooltip title="Click to view warnings" arrow>
                                        <span
                                            style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                                            onClick={() => handleSelectCard(warningsTabIndex)}
                                        >
                                            <WarningAmberIcon color="#D97706" size={20} />
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        ) : null}
                    </Row>
                    
                    <HeaderActions 
                        onClose={handleClose}
                        closeTitle="Close claim preview"
                    />
                </Box>
            </Box>

            <Box
                id='claims-panel'
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    paddingBottom: "60px"
                }}
            >
                {loading ? null : !claimToPreview?.id ? (
                    <ClaimNotFound />
                ) : (
                <>
                {/* Key fields rows */}
                {isDetailView ? null :
                !loading && claimToPreview?.id ? (
                    <ClaimKeyInfoSection claim={claimToPreview} onDenialCodeClick={scrollToDenialCode} />
                ): null}

                {/* Claims & Remits section label */}
                {!loading && claimToPreview?.id && (timelineItems.length > 0 || canRecordAppeal || canManagePayments || canLinkClaim) && !(hasEdiCardList && isDetailView) && (
                    <Box sx={{ px: 2, pt: 1.5, pb: 1, backgroundColor: 'white', display: 'flex', alignItems: 'center', gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.03em', flex: 1, whiteSpace: 'nowrap' }}>
                            CLAIM TIMELINE
                        </Typography>

                        {/* One group, so the actions wrap under the label together rather than one by one */}
                        {claimTimelineActionsEnabled && (canManagePayments || canRecordAppeal || canLinkClaim) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {canManagePayments && (
                                    <TimelineActionButton onClick={() => setIsRecordPaymentOpen(true)}>
                                        + Record remit
                                    </TimelineActionButton>
                                )}
                                {canRecordAppeal && (
                                    <TimelineActionButton onClick={() => setIsRecordAppealOpen(true)}>
                                        + Record action
                                    </TimelineActionButton>
                                )}
                                {canLinkClaim && (
                                    <TimelineActionButton onClick={openLinkDialog}>
                                        Link with other claim
                                    </TimelineActionButton>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* On the corrected claim: what it's the resubmission of */}
                {!loading && !isDetailView && claimToPreview?.id && resubmissionOf && (
                    <Box sx={{ px: 1, pb: 1 }}>
                        <ResubmissionBanner
                            link={resubmissionOf}
                            isOriginal={false}
                            actionOn={resubmissionOf.actionOn}
                            onOpenClaim={handleOpenClaim}
                            onUnlink={canManageLinks ? () => setUnlinkTarget(resubmissionOf) : undefined}
                        />
                    </Box>
                )}

                {/* Card list of EDI objects / log / warnings */}
                {hasEdiCardList && !isDetailView && (
                    <Box sx={{ px: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {timelineItems.map((item) =>
                            item.kind === 'edi' ? (
                                <EdiObjectCard key={`edi-${item.ediObj.id || item.index}`} ediObj={item.ediObj} onClick={() => handleSelectCard(item.index)} />
                            ) : item.kind === 'letter' ? (
                                <AppealLetterCard key={`letter-${item.letter.chatId}`} letter={item.letter} onClick={() => handleSelectLetter(item.letter.chatId)} />
                            ) : item.kind === 'appeal' ? (
                                <AppealCard key={`appeal-${item.appeal.id}`} appeal={item.appeal} onClick={() => handleSelectAppeal(item.appeal.id)} />
                            ) : item.kind === 'linkedRemit' ? (
                                <EdiObjectCard
                                    key={`linked-${item.fromClaimNumber}-${item.ediObj.id || item.index}`}
                                    ediObj={item.ediObj}
                                    fromClaimNumber={item.fromClaimNumber}
                                    onClick={() => handleSelectLinkedRemit(item.index)}
                                />
                            ) : (
                                <AppealLetterPlaceholderCard key="letter-placeholder" billingDate={claimToPreview?.dateSubmitted || detailData?.claim?.dateSubmitted} />
                            )
                        )}
                        {hasLogEntries && (
                            <SimpleNavCard label={`Log (${logEntries.length})`} onClick={() => handleSelectCard(logTabIndex)} />
                        )}
                        {hasWarnings && (
                            <SimpleNavCard
                                label={`Warnings (${warningCount})`}
                                icon={<WarningAmberIcon color="#D97706" size={16} />}
                                labelColor="#D97706"
                                onClick={() => handleSelectCard(warningsTabIndex)}
                            />
                        )}
                    </Box>
                )}

                {/* Back header for the selected card's detail */}
                {hasEdiCardList && isDetailView && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#F9FAFB' }}>
                        <button
                            type="button"
                            onClick={() => {
                                setIsDetailView(false);
                                setSelectedLetterChatId(null);
                                setSelectedAppealId(null);
                                setSelectedLinkedRemitIndex(null);
                            }}
                            className="flex items-center justify-center rounded-[8px] h-[28px] w-[28px] text-gray-600 hover:bg-gray-200 shrink-0 -ml-1"
                            aria-label="Back to Claim timeline"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {selectedAppeal ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                                        {actionTitle(selectedAppeal)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                                        {actionCaption(selectedAppeal, formatDisplayDate)}
                                    </Typography>
                                </Box>
                            ) : selectedLinkedRemit && shownLinkedRemits ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                    <Row sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                                            {getEdiObjectLabel(selectedLinkedRemit)}
                                        </Typography>
                                        <CardChip chip={{ label: `from ${claimTag(shownLinkedRemits.fromClaimNumber)}`, kind: 'default' }} />
                                    </Row>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        {getEdiObjectDate(selectedLinkedRemit)}
                                    </Typography>
                                </Box>
                            ) : selectedLetter ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                    <Row sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                                            Appeal letter
                                        </Typography>
                                        <CardChip chip={{ label: formatVersionCount(detailData?.appealLetters?.length || 0), kind: 'default' }} />
                                    </Row>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                                        First generated {formatDisplayDate(selectedLetter.generatedAt)}
                                        {selectedLetter.generatedBy || selectedLetter.generatedByEmail ? ` · by ${selectedLetter.generatedBy || selectedLetter.generatedByEmail}` : ''}
                                    </Typography>
                                </Box>
                            ) : currentEdiObject ? (
                                <Box id="details-content" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                        <Row sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                                                {getEdiObjectLabel(currentEdiObject)}
                                            </Typography>
                                            {(() => {
                                                const chip = getEdiObjectChip(currentEdiObject);
                                                return chip ? <CardChip chip={chip} /> : null;
                                            })()}
                                            {isManualPayment(currentEdiObject) && <CardChip chip={MANUAL_PAYMENT_CHIP} />}
                                            {currentEdiObject.matchedManual && <CardChip chip={MATCHED_MANUAL_CHIP} />}
                                        </Row>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            {getEdiObjectDate(currentEdiObject)}
                                        </Typography>
                                    </Box>
                                    {(() => {
                                        const { billed, paid } = getEdiObjectAmounts(currentEdiObject);
                                        if (billed === undefined && paid === undefined) return null;
                                        return (
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                {billed !== undefined && (
                                                    <Typography sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>Billed </Box>
                                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{formatCurrency(billed)}</Box>
                                                    </Typography>
                                                )}
                                                {paid !== undefined && (
                                                    <Typography sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>Paid </Box>
                                                        <Box component="span" sx={{ fontWeight: 600, color: paid >= 0 ? 'success.main' : 'text.primary' }}>{formatCurrency(paid)}</Box>
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isWarningsTabSelected ? '#D97706' : 'text.primary' }}>
                                    {isLogTabSelected ? `Log (${logEntries.length})` : isWarningsTabSelected ? `Warnings (${warningCount})` : ''}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Content Area */}
                {(!hasEdiCardList || isDetailView) && (loading ? (
                    <Box sx={{ p: 2 }}>
                        <Skeleton height={20} sx={{ mb: 1 }} />
                        <Skeleton height={20} sx={{ mb: 1 }} />
                        <Skeleton height={20} sx={{ mb: 1 }} />
                        <Skeleton height={100} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : selectedAppeal ? (
                    <AppealDetail
                        key={selectedAppeal.id}
                        appeal={selectedAppeal}
                        claimId={claimToPreview.id}
                        resubmission={selectedAppeal.actionType !== 'correct_resubmit' ? null : selectedAppeal.link ? (
                            <ResubmissionBanner
                                link={selectedAppeal.link}
                                isOriginal
                                onOpenClaim={handleOpenClaim}
                                onUnlink={canManageLinks ? () => setUnlinkTarget(selectedAppeal.link ?? null) : undefined}
                            />
                        ) : canLinkClaim ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', flex: 1 }}>
                                    Not linked to the corrected claim yet.
                                </Typography>
                                <Button size="small" variant="outlined" onClick={openLinkDialog} sx={{ textTransform: 'none' }}>
                                    Link with other claim
                                </Button>
                            </Box>
                        ) : null}
                    />
                ) : selectedLinkedRemit && shownLinkedRemits ? (
                    <Box>
                        <Box sx={{ p: 2, pb: 0 }}>
                            <Alert severity="info" sx={{ maxWidth: '100%' }}>
                                Read-only: a remit of {claimTag(shownLinkedRemits.fromClaimNumber)}, this claim's resubmission.{' '}
                                <Link component="button" type="button" onClick={() => handleOpenClaim(shownLinkedRemits.fromClaimNumber)} sx={{ verticalAlign: 'baseline' }}>
                                    Open {claimTag(shownLinkedRemits.fromClaimNumber)}
                                </Link>
                            </Alert>
                        </Box>
                        <EdiClaimViewer
                            key={`linked-viewer-${selectedLinkedRemitIndex}-${selectedLinkedRemit.id || ''}`}
                            content={JSON.stringify([selectedLinkedRemit])}
                        />
                    </Box>
                ) : selectedLetter ? (
                    <AppealLetterDetail
                        key={selectedLetter.chatId}
                        letter={selectedLetter}
                        letters={detailData?.appealLetters || []}
                        claimNumber={claimToPreview?.claimNumber}
                    />
                ) : isLogTabSelected ? (
                    <Box sx={{ p: 2, backgroundColor: 'white' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {logEntries.map((entry, idx) => (
                                <Box key={entry.id ?? `${entry.timestamp}-${idx}`}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.813rem', fontWeight: 500 }}>
                                            {formatInstant(entry.timestamp)}
                                            {entry.user && ` · ${entry.user}`}
                                        </Typography>
                                        {entry.status && (
                                            <StatusChip status={entry.status as ClaimStatus} />
                                        )}
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.813rem', fontWeight: 500 }}>
                                        {entry.message}
                                    </Typography>
                                    {entry.reason && (
                                        <Box component="ul" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5, pb: 1, pl: 2, m: 0, wordBreak: 'break-word' }}>
                                            {(entry.reason ?? '').split('; ').filter(Boolean).map((part, i) => (
                                                <li key={i}>{part}</li>
                                            ))}
                                        </Box>
                                    )}
                                    {idx < logEntries.length - 1 && (
                                        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', my: 0.5 }} />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : isWarningsTabSelected ? (
                    <Box sx={{ p: 2 }}>
                        {claimWarnings.map((warning, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    p: 1.5,
                                    mb: 1,
                                    backgroundColor: '#FFFBEB',
                                    border: '1px solid #F59E0B',
                                    borderRadius: 1,
                                }}
                            >
                                <WarningAmberIcon color="#D97706" size={20} />
                                <Typography variant="body2" sx={{ color: '#92400E' }}>
                                    {warning}
                                </Typography>
                            </Box>
                        ))}
                        {duplicatePayments.map((pair) => (
                            <DuplicatePaymentWarningCard
                                key={`${pair.warning.manualAttachmentId}-${pair.warning.remitAttachmentId}`}
                                pair={pair}
                                claim={claimToPreview}
                                ediObjects={ediObjects}
                                canJoin={canManagePayments}
                                onJoined={handlePaymentLeftTimeline}
                            />
                        ))}
                    </Box>
                ) : currentEdiObject && isManualPayment(currentEdiObject) ? (
                    <ManualPaymentDetail
                        key={currentEdiObject.attachmentId || currentEdiObject.id}
                        ediObj={currentEdiObject}
                        claim={claimToPreview}
                        ediObjects={ediObjects}
                        canManage={canManagePayments}
                        onRemoved={handlePaymentLeftTimeline}
                        onJoined={handlePaymentLeftTimeline}
                    />
                ) : claimToPreview ? (
                    <Box>
                        {/* A remit a manual payment was joined to */}
                        {currentEdiObject?.matchedManual && (
                            <MatchedManualPaymentNote matched={currentEdiObject.matchedManual} claimId={claimToPreview.id} />
                        )}
                        {/* EDI Viewer */}
                        {currentEdiObject && (() => {
                            const contentString = JSON.stringify([currentEdiObject]);
                            // Use object ID + a hash of the content to ensure uniqueness
                            const contentHash = contentString.length + (currentEdiObject.id || '').length;
                            return (
                                <Box sx={{ p: 0 }} key={`edi-viewer-wrapper-${currentEdiObject.id}-${selectedTab}-${contentHash}`}>
                                    <EdiClaimViewer 
                                        key={`edi-viewer-${currentEdiObject.id}-${selectedTab}-${contentHash}`}
                                        content={contentString} 
                                    />
                                </Box>
                            );
                        })()}
                    </Box>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">No claim selected</Typography>
                    </Box>
                ))}
                </>
                )}
            </Box>

            {canManagePayments && (
                <RecordPaymentDialog
                    open={isRecordPaymentOpen}
                    claim={claimToPreview}
                    ediObjects={ediObjects}
                    payers={detailData?.claim?.payers}
                    onClose={() => setIsRecordPaymentOpen(false)}
                    onRecorded={() => void refreshClaimAfterPayment()}
                />
            )}

            {canRecordAppeal && (
                <RecordAppealDialog
                    open={isRecordAppealOpen}
                    claim={claimToPreview}
                    appealLetters={detailData?.appealLetters || []}
                    appeals={detailData?.appeals}
                    payers={detailData?.claim?.payers}
                    onClose={() => setIsRecordAppealOpen(false)}
                    onRecorded={handleAppealRecorded}
                    onLinkCorrectedClaim={canManageLinks ? handleLinkAfterRecording : undefined}
                />
            )}

            {canManageLinks && (
                <LinkClaimDialog
                    open={isLinkClaimOpen}
                    claim={claimToPreview}
                    appeals={appeals}
                    resubmissionOf={resubmissionOf}
                    afterRecording={isLinkAfterRecording}
                    onClose={() => setIsLinkClaimOpen(false)}
                    onLinked={handleLinkChanged}
                />
            )}

            {canManageLinks && unlinkTarget && (
                <UnlinkClaimDialog
                    open
                    claimId={claimToPreview.id}
                    link={unlinkTarget}
                    onClose={() => setUnlinkTarget(null)}
                    onUnlinked={handleLinkChanged}
                />
            )}

            {
                canAskReve && claimToPreview?.id ?
                    <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 10, pointerEvents: 'none' }}>
                        <AskReveActionMenu
                            disabled={!claimToPreview}
                            onSelectAction={handleAskReveAction}
                            onSelectPredefaultQuestion={handleAskRevePredefaultQuestion}
                        />
                    </Box> : null
            }

            </Box>
            {!isTabletOrSmaller && currentPosition === 'left' && (
                <SidebarDelimiter 
                    onTogglePosition={onTogglePosition}
                    onResizeStart={handleMouseDown} 
                    isResizing={isSidePanelResizing} 
                />
            )}
        </aside>
    );
});

export default ClaimPreview;
